import React, {useState} from 'react';
import {
  Container,
  Box,
  Typography,
  Stack,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useMutation} from '@apollo/client';
import {START_GUIDE_GENERATION} from '../graphql/mutations';
import {useOrg} from '../contexts/OrgContext';
import {auth} from '../config/firebase';
import AIInputComposer, {type GenerationMode} from '../components/AIInputComposer';
import GuideCreationDialog from '../components/GuideCreationDialog';
import {useGoalAnalysisFlow} from '../hooks/useGoalAnalysisFlow';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {EXAMPLE_CHIPS, PERSONA_COLORS} from '../data/exampleChips';

export default function CreateGuidePage() {
  useDocumentTitle('Guides', 'New Guide');

  const {activeOrg} = useOrg();
  const navigate = useNavigate();

  const [prompt, setPrompt] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({open: false, message: '', severity: 'info'});

  const [startGeneration, {loading}] = useMutation(START_GUIDE_GENERATION);
  const [generationJobId, setGenerationJobId] = useState<string | null>(null);
  const [generationDialogOpen, setGenerationDialogOpen] = useState(false);

  const getAuthToken = async () => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('Failed to get authentication token');
    }
    return token;
  };

  const callGenerateGuide = async (
    promptText: string,
    planInput?: Record<string, unknown>,
    attachmentIds?: string[],
  ) => {
    if (!activeOrg) {
      setSnackbar({open: true, message: 'Please create or select an organization first', severity: 'error'});
      return;
    }

    const token = await getAuthToken();

    const {data} = await startGeneration({
      variables: {
        prompt: promptText,
        orgSlug: activeOrg.slug,
        ...(planInput && {planInput}),
        ...(attachmentIds && attachmentIds.length > 0 && {attachmentIds}),
      },
      context: {headers: {authorization: `Bearer ${token}`}},
    });

    const result = data?.startGuideGeneration;
    if (result?.deduplicated && result?.resultRef) {
      navigate(`/org/guides/${result.resultRef}/edit`);
    } else if (result?.jobId) {
      setPrompt('');
      setGenerationJobId(result.jobId);
      setGenerationDialogOpen(true);
    }
  };

  const {startAnalysis, isAnalyzing, isGenerating, dialogElement} = useGoalAnalysisFlow({
    onGenerate: async (payload) => {
      const planInput: Record<string, unknown> = {
        prompt: payload.prompt,
        analysisResult: payload.analysisResult,
        clarifyingAnswers: payload.clarifyingAnswers,
      };
      await callGenerateGuide(payload.prompt, planInput, payload.attachmentIds);
    },
    onSkip: async (payload) => {
      await callGenerateGuide(payload.prompt, undefined, payload.attachmentIds);
    },
  });

  const handleComposerSubmit = async (text: string, attachmentIds: string[], mode: GenerationMode) => {
    if (!text.trim() && attachmentIds.length === 0) return;

    if (!activeOrg) {
      setSnackbar({open: true, message: 'Please create or select an organization first', severity: 'error'});
      return;
    }

    try {
      if (mode === 'analyze') {
        await startAnalysis(text, attachmentIds);
      } else {
        await callGenerateGuide(text, undefined, attachmentIds);
      }
    } catch (error: unknown) {
      console.error('Error:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to generate',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({...snackbar, open: false});
  };

  const isLoading = isAnalyzing || loading || isGenerating;

  return (
    <Box sx={{flex: 1, bgcolor: 'background.default'}}>
      <Container maxWidth="md" sx={{py: 4}}>
        <Typography variant="h4" component="h1" sx={{fontWeight: 500, mb: 1}}>
          Create Guide
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{mb: 4}}>
          Describe a guide and AI will generate the full template with steps, deadlines, and FAQs.
          Publishing under <strong>{activeOrg?.name}</strong> ({activeOrg?.slug}).
        </Typography>

        <Stack spacing={3}>
          <AIInputComposer
            value={prompt}
            onChange={setPrompt}
            onSubmit={handleComposerSubmit}
            loading={isLoading}
            placeholder="Describe the guide you want to create..."
          />
        </Stack>

        {/* Persona-grouped example chips (same as home page) */}
        <Box sx={{mt: 4}}>
          <Stack spacing={2.5}>
            {EXAMPLE_CHIPS.map((group) => {
              const palette = PERSONA_COLORS[group.persona];
              return (
                <Box key={group.persona}>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{mb: 1}}>
                    <Typography sx={{fontSize: '1rem', lineHeight: 1}}>{group.icon}</Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: palette.text,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                      }}
                    >
                      {group.persona}
                    </Typography>
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" gap={0.75}>
                    {group.chips.map((chip) => (
                      <Paper
                        key={chip.label}
                        elevation={0}
                        onClick={() => setPrompt(chip.promptText)}
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          borderRadius: '20px',
                          cursor: 'pointer',
                          bgcolor: palette.bg,
                          border: '1px solid',
                          borderColor: palette.border,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${palette.border}80`,
                            borderColor: palette.accent,
                          },
                          '&:active': {transform: 'translateY(0)'},
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{fontWeight: 500, color: palette.text, fontSize: '0.8rem'}}
                        >
                          {chip.label}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Container>

      {/* Clarification Dialog (analyze mode) */}
      {dialogElement}

      {/* Guide Creation Progress Dialog */}
      <GuideCreationDialog
        open={generationDialogOpen}
        jobId={generationJobId}
        onClose={() => { setGenerationDialogOpen(false); setGenerationJobId(null); }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{width: '100%'}}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
